import React, { useEffect, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { supabase } from '../../lib/supabase';

/**
 * Reusable editor for `agencies.creator_network_ids` — TikTok Backstage
 * faction_id per server (a.k.a. "Creator Network ID" in the Backstage UI).
 *
 * Renders one input per server in `agency.servers`. On blur or Enter the row
 * is upserted into the agency's `creator_network_ids` jsonb. Tenant_admin /
 * super_admin / agency_owner all share this component.
 */
const CreatorNetworkIdsEditor = ({ agency, readOnly = false, onSaved, compact = false }) => {
  const { t } = useTranslation();
  const [values, setValues] = useState(agency?.creator_network_ids ?? {});
  const [savingKey, setSavingKey] = useState(null);
  const [error, setError] = useState(null);

  useEffect(() => {
    setValues(agency?.creator_network_ids ?? {});
  }, [agency?.creator_network_ids]);

  if (!agency) return null;
  const servers = agency.servers ?? [];

  const persist = async (server, value) => {
    const trimmed = (value ?? '').trim();
    const current = (agency.creator_network_ids ?? {})[server] ?? '';
    if (trimmed === current) return; // no-op
    setSavingKey(server);
    setError(null);
    const next = { ...(agency.creator_network_ids ?? {}) };
    if (trimmed) next[server] = trimmed;
    else delete next[server];
    const { error: dbErr } = await supabase
      .from('agencies')
      .update({ creator_network_ids: next })
      .eq('id', agency.id);
    setSavingKey(null);
    if (dbErr) {
      setError(dbErr.message);
      setValues(agency.creator_network_ids ?? {});
      return;
    }
    onSaved?.(next);
  };

  return (
    <div>
      {!compact && (
        <div className="text-xs uppercase tracking-widest text-muted-foreground font-semibold mb-2">
          {t('tcn.admin.cnid.heading', 'Creator Network ID')}
          <span className="ms-2 normal-case font-normal text-muted-foreground/80">
            {t('tcn.admin.cnid.subheading', 'TikTok Backstage faction_id, one per server')}
          </span>
        </div>
      )}
      {servers.length === 0 ? (
        <p className="text-xs text-muted-foreground">
          {t('tcn.admin.cnid.noServers', 'No servers configured for this agency.')}
        </p>
      ) : (
        <div className="grid gap-2" style={{ gridTemplateColumns: 'minmax(60px,auto) 1fr' }}>
          {servers.map((server) => {
            const v = values[server] ?? '';
            const saving = savingKey === server;
            return (
              <React.Fragment key={server}>
                <label
                  htmlFor={`cnid-${agency.id}-${server}`}
                  className="text-xs font-data text-foreground self-center"
                >
                  {server}
                </label>
                <input
                  id={`cnid-${agency.id}-${server}`}
                  type="text"
                  inputMode="numeric"
                  value={v}
                  readOnly={readOnly}
                  disabled={readOnly || saving}
                  placeholder={t('tcn.admin.cnid.placeholder', 'e.g. 108135')}
                  onChange={(e) => setValues({ ...values, [server]: e.target.value })}
                  onBlur={(e) => persist(server, e.target.value)}
                  onKeyDown={(e) => {
                    if (e.key === 'Enter') {
                      e.preventDefault();
                      e.currentTarget.blur();
                    }
                  }}
                  className="px-3 py-2 text-base rounded-lg bg-card text-foreground border border-border focus:outline-none focus:ring-2 focus:ring-ring font-data"
                />
              </React.Fragment>
            );
          })}
        </div>
      )}
      {error && <p className="text-xs text-error mt-2">{error}</p>}
    </div>
  );
};

export default CreatorNetworkIdsEditor;
